import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Image as ImageIcon, Edit2, Save, X, CheckCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function OnboardingStep2() {
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [assets, setAssets] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", link: "" });
  const [initializing, setInitializing] = useState(true);
  const [autoFetching, setAutoFetching] = useState(false);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    initializeStep();
  }, []);

  const initializeStep = async () => {
    try {
      setInitializing(true);
      setAutoFetching(false);
      setError(null);

      let fetchedProducts = [];
      const productsResponse = await apiService.getTailspinProducts();
      if (productsResponse.data.success) {
        fetchedProducts = productsResponse.data.data || [];
      }

      const defaultProduct = fetchedProducts.length > 0 ? fetchedProducts[0] : null;
      const defaultQuery = defaultProduct ? `${defaultProduct.name} dashboard` : "";

      setProducts(fetchedProducts);
      setSelectedProduct(defaultProduct);
      setSearchQuery(defaultQuery);

      let fetchedAssets = [];
      const assetsResponse = await apiService.getTailspinAssets();
      if (assetsResponse.data.success) {
        // New API returns images directly, convert to old format for compatibility
        const images = assetsResponse.data.images || [];
        if (images.length > 0) {
          fetchedAssets = [{
            tag: 'brand-images',
            type: 'images',
            description: JSON.stringify(images)
          }];
        }
      }

      if (fetchedAssets.length === 0) {
        if (!defaultProduct) {
          setError("We couldn't find any products yet. Please complete Step 1 to add products.");
        } else if (defaultQuery.trim()) {
          setAutoFetching(true);
          const tag = `${defaultProduct.name.toLowerCase().replace(/\s+/g, '-')}-images`;
          const fetchResponse = await apiService.fetchAssets(defaultQuery, tag, "product-images");

          if (fetchResponse.data.success) {
            const newAsset = fetchResponse.data.data.asset;
            fetchedAssets = newAsset ? [newAsset] : [];
          } else {
            setError(fetchResponse.data.message || "Failed to fetch assets automatically.");
          }
        }
      }

      setAssets(fetchedAssets);
    } catch (err) {
      console.error("Error initializing onboarding step 2:", err);
      setError("Failed to prepare your product assets automatically. You can search manually.");
    } finally {
      setAutoFetching(false);
      setInitializing(false);
    }
  };

  const handleEditAsset = (asset, imageIndex) => {
    const images = JSON.parse(asset.description);
    const image = images[imageIndex];
    setEditingTag(`${asset.tag}-${imageIndex}`);
    setEditForm({
      title: image.title,
      link: image.link
    });
  };

  const handleSaveEdit = async (asset, imageIndex) => {
    try {
      const images = JSON.parse(asset.description);
      images[imageIndex] = editForm;

      // Use new bulk update API
      const updates = [
        { key: "images", value: images, value_type: "json" }
      ];
      const response = await apiService.updateTailspinAssets(updates);

      if (response.data.success) {
        setAssets(assets.map(a =>
          a.tag === asset.tag ? { ...a, description: JSON.stringify(images) } : a
        ));
        setEditingTag(null);
      } else {
        setError(response.data.message || "Failed to update asset");
      }
    } catch (err) {
      console.error("Error updating asset:", err);
      setError("Failed to update asset");
    }
  };

  const handleCancelEdit = () => {
    setEditingTag(null);
    setEditForm({ title: "", link: "" });
  };

  const handleCompleteOnboarding = async () => {
    if (assets.length === 0) {
      setError("Please add at least one asset before completing onboarding");
      return;
    }

    try {
      setCompleting(true);
      setError(null);
      const response = await apiService.completeOnboarding();

      if (response.data.success) {
        try {
          await checkAuthStatus();
        } catch (authError) {
          console.error("Error refreshing auth status after onboarding:", authError);
        }
        navigate("/tailspin/dashboard");
      } else {
        setError(response.data.message || "Failed to complete onboarding");
      }
    } catch (err) {
      console.error("Error completing onboarding:", err);
      setError("Failed to complete onboarding");
    } finally {
      setCompleting(false);
    }
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>
            {autoFetching ? "Finding product assets for you..." : "Preparing your products..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, rgba(255, 107, 90, 0.1), #F8F7F4, rgba(215, 180, 114, 0.1))' }}>
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(215, 180, 114, 0.15)' }}>
            <ImageIcon className="w-8 h-8" style={{ color: '#D7B472' }} />
          </div>
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#0A0A10' }}>
            Almost There!
          </h1>
          <p className="text-xl mb-2" style={{ color: '#0A0A10', opacity: 0.7 }}>
            Step 2: Add Product Assets
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm" style={{ backgroundColor: '#FF6B5A' }}>
              <CheckCircle className="w-5 h-5" />
            </div>
            <div className="w-16 h-1" style={{ backgroundColor: '#FF6B5A' }}></div>
            <div className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: '#FF6B5A' }}>
              2
            </div>
          </div>
        </div>

        {/* Auto Fetch Info */}
        <div className="rounded-xl shadow-sm border p-8 mb-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <h2 className="text-2xl font-semibold mb-3" style={{ color: '#0A0A10' }}>
            Finding Product Images
          </h2>
          <p style={{ color: '#0A0A10', opacity: 0.7 }}>
            We automatically searched for{" "}
            {selectedProduct ? (
              <span className="font-semibold" style={{ color: '#0A0A10' }}>
                {selectedProduct.name} dashboard
              </span>
            ) : (
              "your product dashboards"
            )}
            {" "}to gather screenshots and assets. Review and edit them below.
          </p>
          {error && (
            <div className="mt-4 p-4 border rounded-lg" style={{ borderColor: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
              {error}
            </div>
          )}
        </div>

        {/* Assets Display */}
        <div className="rounded-xl shadow-sm border p-8 mb-8" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
          <h2 className="text-2xl font-semibold mb-6" style={{ color: '#0A0A10' }}>
            Your Assets ({assets.length})
          </h2>

          {assets.length === 0 ? (
            <div className="text-center py-12" style={{ color: '#0A0A10', opacity: 0.7 }}>
              <ImageIcon className="w-16 h-16 mx-auto mb-4" style={{ color: '#0A0A10', opacity: 0.5 }} />
              <p className="text-lg mb-2">No assets yet</p>
              <p className="text-sm">Search for product images to get started</p>
            </div>
          ) : (
            <div className="space-y-8">
              {assets.map((asset) => {
                const images = JSON.parse(asset.description);
                return (
                  <div key={asset.tag} className="border rounded-lg p-6" style={{ borderColor: '#D7B472', backgroundColor: '#F8F7F4' }}>
                    <h3 className="text-lg font-semibold mb-4 capitalize" style={{ color: '#0A0A10' }}>
                      {asset.tag.replace(/-/g, ' ')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {images.map((image, index) => (
                        <div
                          key={index}
                          className="border rounded-lg overflow-hidden transition-colors"
                          style={{ borderColor: '#D7B472' }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                        >
                          {editingTag === `${asset.tag}-${index}` ? (
                            <div className="p-4 space-y-3">
                              <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: '#0A0A10' }}>
                                  Title
                                </label>
                                <input
                                  type="text"
                                  value={editForm.title}
                                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:outline-none"
                                  style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                                  onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                                  onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1" style={{ color: '#0A0A10' }}>
                                  Link
                                </label>
                                <input
                                  type="text"
                                  value={editForm.link}
                                  onChange={(e) => setEditForm({ ...editForm, link: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:outline-none"
                                  style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                                  onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B5A'; e.currentTarget.style.ringColor = '#FF6B5A'; }}
                                  onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                                />
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveEdit(asset, index)}
                                  className="flex-1 px-3 py-2 text-white text-sm rounded flex items-center justify-center gap-1"
                                  style={{ backgroundColor: '#FF6B5A' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                                >
                                  <Save className="w-3 h-3" />
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="flex-1 px-3 py-2 text-sm rounded flex items-center justify-center gap-1"
                                  style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.5'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                                >
                                  <X className="w-3 h-3" />
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="relative aspect-video" style={{ backgroundColor: '#D7B472', opacity: 0.1 }}>
                                <img
                                  src={image.link}
                                  alt={image.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                                  }}
                                />
                                <button
                                  onClick={() => handleEditAsset(asset, index)}
                                  className="absolute top-2 right-2 p-2 rounded-lg shadow-md transition-colors"
                                  style={{ backgroundColor: '#F8F7F4' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.1)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F8F7F4'; }}
                                >
                                  <Edit2 className="w-4 h-4" style={{ color: '#0A0A10' }} />
                                </button>
                              </div>
                              <div className="p-3">
                                <p className="text-sm font-medium line-clamp-2" style={{ color: '#0A0A10' }}>
                                  {image.title}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate("/tailspin/onboarding/step1")}
            className="px-8 py-3 rounded-lg font-medium text-lg transition-colors"
            style={{ backgroundColor: '#D7B472', opacity: 0.3, color: '#0A0A10' }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          >
            ← Back to Products
          </button>
          <button
            onClick={handleCompleteOnboarding}
            disabled={completing || assets.length === 0}
            className="px-8 py-3 text-white rounded-lg disabled:cursor-not-allowed font-medium text-lg transition-colors flex items-center gap-2"
            style={{ backgroundColor: (completing || assets.length === 0) ? '#D7B472' : '#FF6B5A', opacity: (completing || assets.length === 0) ? 0.5 : 1 }}
            onMouseEnter={(e) => { if (!completing && assets.length > 0) e.currentTarget.style.backgroundColor = '#e55a4a'; }}
            onMouseLeave={(e) => { if (!completing && assets.length > 0) e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
          >
            {completing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Complete Onboarding
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

